import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';

// Formátování čísel s mezerami jako oddělovači tisíců
const formatNumber = (num) => {
  return new Intl.NumberFormat('cs-CZ', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 1 
  }).format(num);
};

// Formátování koeficientů s přesností na 2 desetinná místa
const formatCoefficient = (num) => {
  return new Intl.NumberFormat('cs-CZ', { 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }).format(num);
};

export default function SummaryTab({ userRole, currentUserId, isDarkMode }) {
  const [lastMonthCommissions, setLastMonthCommissions] = useState(0); // Provize za minulý měsíc
  const [currentMonthCommissions, setCurrentMonthCommissions] = useState(0); // Předběžné provize za aktuální měsíc
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(currentUserId);
  const [allUsers, setAllUsers] = useState([]);
  
  // Detailní rozklad komisí
  const [lastMonthBreakdown, setLastMonthBreakdown] = useState({
    main: 0,
    strategic: 0,
    cashback: 0,
    cashbackStrategic: 0,
    gp: 0
  });
  const [currentMonthBreakdown, setCurrentMonthBreakdown] = useState({
    main: 0,
    strategic: 0,
    cashback: 0,
    cashbackStrategic: 0,
    gp: 0
  });
  
  // Detailní data komisí pro fakturu
  const [lastMonthCommissionsDetail, setLastMonthCommissionsDetail] = useState({
    main: [],
    strategic: [],
    cashback: [],
    cashbackStrategic: []
  });
  
  // Data pro vybraný měsíc faktury
  const [invoiceMonthCommissions, setInvoiceMonthCommissions] = useState(0);
  const [invoiceMonthRetention, setInvoiceMonthRetention] = useState(0);
  const [invoiceMonthDetail, setInvoiceMonthDetail] = useState({
    main: [],
    strategic: [],
    cashback: [],
    cashbackStrategic: []
  });
  
  // Stavy pro zádržné
  const [lastMonthRetention, setLastMonthRetention] = useState(0);
  const [currentMonthRetention, setCurrentMonthRetention] = useState(0);
  const [monthlyRetentionData, setMonthlyRetentionData] = useState([]);
  
  // BMSL data
  const [lastMonthBmsl, setLastMonthBmsl] = useState({ totalBmsl: 0, companyCount: 0, companies: [] });
  const [currentMonthBmsl, setCurrentMonthBmsl] = useState({ totalBmsl: 0, companyCount: 0, companies: [] });
  
  // Modal pro seznam BMSL firem
  const [showBmslModal, setShowBmslModal] = useState(false);
  const [bmslModalData, setBmslModalData] = useState({ companies: [], month: '', isLastMonth: false });
  
  // Koeficienty
  const [coefficients, setCoefficients] = useState({ mainCoefficient: 1.0, strategicCoefficient: 1.0 });
  
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const getLastMonth = () => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [invoiceMonth, setInvoiceMonth] = useState(() => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [invoiceData, setInvoiceData] = useState({
    supplierName: '',
    supplierAddress: '',
    supplierIco: '',
    supplierDic: '',
    accountNumber: '',
    variableSymbol: '',
    iban: '',
    isVatPayer: false
  });

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const currentMonth = getCurrentMonth();
      const lastMonth = getLastMonth();
      const targetUserId = selectedUser || currentUserId;
      const filterParam = targetUserId !== currentUserId ? `&filterUserId=${targetUserId}` : '';

      // Načti všechny typy provizí za minulý měsíc
      const lastMonthPromises = [
        fetch(`${API_URL}/commissions?month=${lastMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/strategic-commissions?month=${lastMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/cashbacks?month=${lastMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/cashback-strategic-commissions?month=${lastMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`)
      ];

      const [lastMainRes, lastStrategicRes, lastCashbackRes, lastCashbackStrategicRes] = await Promise.all(lastMonthPromises);
      
      let lastMonthTotal = 0;
      let lastBreakdown = { main: 0, strategic: 0, cashback: 0, cashbackStrategic: 0, gp: 0 };
      let lastCommissionsDetail = { main: [], strategic: [], cashback: [], cashbackStrategic: [] };
      
      if (lastMainRes.ok) {
        const mainData = await lastMainRes.json();
        lastCommissionsDetail.main = mainData.commissions || [];
        lastBreakdown.main = mainData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        lastBreakdown.gp = mainData.commissions?.reduce((sum, c) => sum + (c.gp || 0), 0) || 0;
        lastMonthTotal += lastBreakdown.main;
      }
      
      if (lastStrategicRes.ok) {
        const strategicData = await lastStrategicRes.json();
        lastCommissionsDetail.strategic = strategicData.commissions || [];
        lastBreakdown.strategic = strategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        lastMonthTotal += lastBreakdown.strategic;
      }
      
      if (lastCashbackRes.ok) {
        const cashbackData = await lastCashbackRes.json();
        lastCommissionsDetail.cashback = cashbackData.cashbacks || [];
        lastBreakdown.cashback = cashbackData.cashbacks?.reduce((sum, c) => sum + (c.cashback || 0), 0) || 0;
        lastMonthTotal += lastBreakdown.cashback;
      }
      
      if (lastCashbackStrategicRes.ok) {
        const cashbackStrategicData = await lastCashbackStrategicRes.json();
        lastCommissionsDetail.cashbackStrategic = cashbackStrategicData.commissions || [];
        lastBreakdown.cashbackStrategic = cashbackStrategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        lastMonthTotal += lastBreakdown.cashbackStrategic;
      }
      
      setLastMonthCommissions(lastMonthTotal);
      setLastMonthBreakdown(lastBreakdown);
      setLastMonthCommissionsDetail(lastCommissionsDetail);

      // Načti všechny typy provizí za aktuální měsíc
      const currentMonthPromises = [
        fetch(`${API_URL}/commissions?month=${currentMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/strategic-commissions?month=${currentMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/cashbacks?month=${currentMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/cashback-strategic-commissions?month=${currentMonth}&userId=${currentUserId}&filterUserId=${targetUserId}`)
      ];

      const [currentMainRes, currentStrategicRes, currentCashbackRes, currentCashbackStrategicRes] = await Promise.all(currentMonthPromises);
      
      let currentTotal = 0;
      let currentBreakdown = { main: 0, strategic: 0, cashback: 0, cashbackStrategic: 0, gp: 0 };
      
      if (currentMainRes.ok) {
        const mainData = await currentMainRes.json();
        currentBreakdown.main = mainData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        currentBreakdown.gp = mainData.commissions?.reduce((sum, c) => sum + (c.gp || 0), 0) || 0;
        currentTotal += currentBreakdown.main;
      }
      
      if (currentStrategicRes.ok) {
        const strategicData = await currentStrategicRes.json();
        currentBreakdown.strategic = strategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        currentTotal += currentBreakdown.strategic;
      }
      
      if (currentCashbackRes.ok) {
        const cashbackData = await currentCashbackRes.json();
        currentBreakdown.cashback = cashbackData.cashbacks?.reduce((sum, c) => sum + (c.cashback || 0), 0) || 0;
        currentTotal += currentBreakdown.cashback;
      }
      
      if (currentCashbackStrategicRes.ok) {
        const cashbackStrategicData = await currentCashbackStrategicRes.json();
        currentBreakdown.cashbackStrategic = cashbackStrategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        currentTotal += currentBreakdown.cashbackStrategic;
      }
      
      setCurrentMonthCommissions(currentTotal);
      setCurrentMonthBreakdown(currentBreakdown);

      // Načti data o zádržném pro oba měsíce
      const [lastRetentionRes, currentRetentionRes] = await Promise.all([
        fetch(`${API_URL}/retention/monthly?userId=${targetUserId}&month=${lastMonth}`),
        fetch(`${API_URL}/retention/monthly?userId=${targetUserId}&month=${currentMonth}`)
      ]);

      let lastRetention = 0;
      let currentRetention = 0;

      if (lastRetentionRes.ok) {
        const retentionData = await lastRetentionRes.json();
        const record = retentionData.records?.[0];
        lastRetention = record?.retentionAmount || 0;
      }

      if (currentRetentionRes.ok) {
        const retentionData = await currentRetentionRes.json();
        const record = retentionData.records?.[0];
        currentRetention = record?.retentionAmount || 0;
      }

      setLastMonthRetention(lastRetention);
      setCurrentMonthRetention(currentRetention);

      // Načti BMSL data pro minulý a aktuální měsíc
      const [lastBmslRes, currentBmslRes] = await Promise.all([
        fetch(`${API_URL}/user/bmsl?userId=${targetUserId}&month=${lastMonth}`),
        fetch(`${API_URL}/user/bmsl?userId=${targetUserId}&month=${currentMonth}`)
      ]);

      if (lastBmslRes.ok) {
        const bmslData = await lastBmslRes.json();
        setLastMonthBmsl({
          totalBmsl: bmslData.totalBmsl || 0,
          companyCount: bmslData.companyCount || 0,
          companies: bmslData.companies || []
        });
      }

      if (currentBmslRes.ok) {
        const bmslData = await currentBmslRes.json();
        setCurrentMonthBmsl({
          totalBmsl: bmslData.totalBmsl || 0,
          companyCount: bmslData.companyCount || 0,
          companies: bmslData.companies || []
        });
      }

      // Načti koeficienty uživatele
      const coeffRes = await fetch(`${API_URL}/user/coefficients?userId=${targetUserId}`);
      if (coeffRes.ok) {
        const coeffData = await coeffRes.json();
        setCoefficients({
          mainCoefficient: coeffData.mainCoefficient || 1.0,
          strategicCoefficient: coeffData.strategicCoefficient || 1.0
        });
      }

      // Načti data za posledních 12 měsíců pro graf (všechny typy komisí)
      const now = new Date();
      const monthlyPromises = [];
      for (let i = 0; i < 12; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        // Pro každý měsíc načti všechny typy komisí
        monthlyPromises.push(
          Promise.all([
            fetch(`${API_URL}/commissions?month=${monthStr}&userId=${currentUserId}&filterUserId=${targetUserId}`).then(res => 
              res.ok ? res.json() : { commissions: [] }
            ),
            fetch(`${API_URL}/strategic-commissions?month=${monthStr}&userId=${currentUserId}&filterUserId=${targetUserId}`).then(res => 
              res.ok ? res.json() : { commissions: [] }
            ),
            fetch(`${API_URL}/cashbacks?month=${monthStr}&userId=${currentUserId}&filterUserId=${targetUserId}`).then(res => 
              res.ok ? res.json() : { cashbacks: [] }
            ),
            fetch(`${API_URL}/cashback-strategic-commissions?month=${monthStr}&userId=${currentUserId}&filterUserId=${targetUserId}`).then(res => 
              res.ok ? res.json() : { commissions: [] }
            )
          ])
        );
      }

      const monthlyResults = await Promise.all(monthlyPromises);
      const chartData = monthlyResults.map((results, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        
        const [mainData, strategicData, cashbackData, cashbackStrategicData] = results;
        
        let total = 0;
        total += mainData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        total += strategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        total += cashbackData.cashbacks?.reduce((sum, c) => sum + (c.cashback || 0), 0) || 0;
        total += cashbackStrategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
        
        return {
          month: date.toLocaleDateString('cs-CZ', { year: 'numeric', month: 'short' }),
          amount: total
        };
      }).reverse();
      
      setMonthlyData(chartData);

      // Načti měsíční zádržné data za posledních 12 měsíců
      const retentionPromises = [];
      for (let i = 0; i < 12; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        retentionPromises.push(
          fetch(`${API_URL}/retention/monthly?userId=${targetUserId}&month=${monthStr}`).then(res => 
            res.ok ? res.json() : { records: [] }
          )
        );
      }

      const retentionResults = await Promise.all(retentionPromises);
      const retentionData = retentionResults.map((result, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        
        const record = result.records?.[0];
        
        return {
          month: date.toLocaleDateString('cs-CZ', { year: 'numeric', month: 'short' }),
          retentionAmount: record?.retentionAmount || 0,
          releasedAmount: record?.releasedAmount || 0,
          currentFundBalance: record?.currentFundBalance || 0
        };
      }).reverse();
      
      setMonthlyRetentionData(retentionData);
    } catch (error) {
      console.error('Chyba při načítání přehledu:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceMonthData = async (month) => {
    try {
      const targetUserId = selectedUser || currentUserId;

      // Načti všechny typy provizí pro vybraný měsíc
      const promises = [
        fetch(`${API_URL}/commissions?month=${month}&userId=${targetUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/strategic-commissions?month=${month}&userId=${targetUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/cashbacks?month=${month}&userId=${targetUserId}&filterUserId=${targetUserId}`),
        fetch(`${API_URL}/cashback-strategic-commissions?month=${month}&userId=${targetUserId}&filterUserId=${targetUserId}`)
      ];

      const [mainRes, strategicRes, cashbackRes, cashbackStrategicRes] = await Promise.all(promises);
      
      let total = 0;
      let detail = { main: [], strategic: [], cashback: [], cashbackStrategic: [] };
      
      if (mainRes.ok) {
        const mainData = await mainRes.json();
        detail.main = mainData.commissions || [];
        total += mainData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
      }
      
      if (strategicRes.ok) {
        const strategicData = await strategicRes.json();
        detail.strategic = strategicData.commissions || [];
        total += strategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
      }
      
      if (cashbackRes.ok) {
        const cashbackData = await cashbackRes.json();
        detail.cashback = cashbackData.cashbacks || [];
        total += cashbackData.cashbacks?.reduce((sum, c) => sum + (c.cashback || 0), 0) || 0;
      }
      
      if (cashbackStrategicRes.ok) {
        const cashbackStrategicData = await cashbackStrategicRes.json();
        detail.cashbackStrategic = cashbackStrategicData.commissions || [];
        total += cashbackStrategicData.commissions?.reduce((sum, c) => sum + (c.commission || 0), 0) || 0;
      }
      
      setInvoiceMonthCommissions(total);
      setInvoiceMonthDetail(detail);

      // Načti zádržné pro vybraný měsíc
      const retentionRes = await fetch(`${API_URL}/retention/monthly?userId=${targetUserId}&month=${month}`);
      if (retentionRes.ok) {
        const retentionData = await retentionRes.json();
        const record = retentionData.records?.[0];
        setInvoiceMonthRetention(record?.retentionAmount || 0);
      } else {
        setInvoiceMonthRetention(0);
      }
    } catch (error) {
      console.error('Chyba při načítání dat měsíce faktury:', error);
    }
  };

  const payCashbackFromRetention = async () => {
    if (!window.confirm('Opravdu chcete zaplatit cashback ze zádrženého? Tato akce sníží cashback na 0 a uvolní prostředky ze zádržného fondu.')) {
      return;
    }

    setLoading(true);
    try {
      const targetUserId = selectedUser || currentUserId;
      const lastMonth = getLastMonth();
      
      const response = await fetch(`${API_URL}/retention/pay-cashback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: targetUserId,
          month: lastMonth
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}\nNový stav fondu: ${formatNumber(data.newFundBalance)} Kč`);
        // Znovu načti data
        await fetchSummaryData();
      } else {
        const error = await response.json();
        alert(`❌ Chyba: ${error.error}`);
      }
    } catch (error) {
      console.error('Chyba při placení cashbacku:', error);
      alert('❌ Chyba při placení cashbacku ze zádržného');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = () => {
    // Použij hodnotu k výplatě (po odečtení zádržného)
    const payoutAmount = invoiceMonthCommissions - invoiceMonthRetention;
    if (!invoiceData.accountNumber || !payoutAmount) return '';
    const baseAmount = Math.round(payoutAmount * 100) / 100;
    const vatAmount = invoiceData.isVatPayer
      ? Math.round(baseAmount * 0.21 * 100) / 100
      : 0;
    const qrAmount = invoiceData.isVatPayer
      ? Math.round((baseAmount + vatAmount) * 100) / 100
      : baseAmount;
    
    // Převod českého čísla účtu na IBAN pro QR kód
    let accountInput = invoiceData.accountNumber.trim().replace(/\s/g, '');
    let iban = '';
    
    // Pokud to ještě není IBAN, převedeme český účet na IBAN
    if (!accountInput.startsWith('CZ')) {
      const parts = accountInput.split('/');
      if (parts.length === 2) {
        const bankCode = parts[1].padStart(4, '0');
        const accountParts = parts[0].split('-');
        
        let prefix = '';
        let accountNumber = '';
        
        if (accountParts.length === 2) {
          prefix = accountParts[0].padStart(6, '0');
          accountNumber = accountParts[1].padStart(10, '0');
        } else {
          prefix = '000000';
          accountNumber = accountParts[0].padStart(10, '0');
        }
        
        // BBAN = bankCode(4) + prefix(6) + accountNumber(10) = 20 číslic
        const bban = bankCode + prefix + accountNumber;
        
        // Výpočet kontrolního součtu pro IBAN
        // 1. Vytvoříme string: BBAN + "CZ00" převedené na čísla (C=12, Z=35, 0=0, 0=0)
        const ibanCheck = bban + '123500'; // CZ = 1235, 00 = 00
        
        // 2. Vypočítáme modulo 97
        let remainder = 0;
        for (let i = 0; i < ibanCheck.length; i++) {
          remainder = (remainder * 10 + parseInt(ibanCheck[i])) % 97;
        }
        
        // 3. Kontrolní součet = 98 - zbytek
        const checksum = (98 - remainder).toString().padStart(2, '0');
        iban = 'CZ' + checksum + bban;
      } else {
        iban = accountInput;
      }
    } else {
      iban = accountInput.replace(/\s/g, '');
    }
    
    // Český QR platební standard - Short Payment Descriptor verze 1.0
    const amount = qrAmount.toFixed(2);
    const vs = invoiceData.variableSymbol || '';
    
    // Správný formát: SPD*1.0*ACC:IBAN*AM:částka*CC:CZK*X-VS:VS*
    const qrData = `SPD*1.0*ACC:${iban}*AM:${amount}*CC:CZK*X-VS:${vs}*`;
    
    console.log('QR Data:', qrData);
    console.log('IBAN:', iban);
    console.log('Original account:', invoiceData.accountNumber);
    
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`;
  };

  const generateInvoicePDF = () => {
    // Formátování čísel s čárkou jako desetinným oddělovačem
    const formatCurrency = (num) => {
      return num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    
    // Formátuj měsíc pro zobrazení
    const [year, month] = invoiceMonth.split('-');
    const monthName = new Date(year, month - 1).toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    
    // Použij hodnotu "K výplatě" (provize - zádržné)
    const payoutAmount = invoiceMonthCommissions - invoiceMonthRetention;
    const baseAmount = Math.round(payoutAmount * 100) / 100;
    const vatAmount = invoiceData.isVatPayer ? Math.round(baseAmount * 0.21 * 100) / 100 : 0;
    const totalAmount = Math.round((baseAmount + vatAmount) * 100) / 100;
    
    // Vytvoř seznam zákazníků a produktů (každý na řádek)
    let customerListItems = '';
    
    // Hlavní provize - zákazníci
    invoiceMonthDetail.main.forEach(comm => {
      if (comm.commission > 0) {
        customerListItems += `<li style="margin-bottom: 3px;">${comm.companyName || 'N/A'} (IČO: ${comm.ico || 'N/A'})</li>`;
      }
    });
    
    // Strategické provize - zákazníci a produkty
    invoiceMonthDetail.strategic.forEach(comm => {
      if (comm.commission > 0) {
        const productType = comm.productType || comm.productName || 'strategický produkt';
        customerListItems += `<li style="margin-bottom: 3px;">${comm.companyName || 'N/A'} (IČO: ${comm.ico || 'N/A'}) - ${productType}</li>`;
      }
    });
    
    // Cashback - zákazníci
    invoiceMonthDetail.cashback.forEach(cb => {
      if (cb.cashback !== 0) {
        customerListItems += `<li style="margin-bottom: 3px;">${cb.companyName || 'N/A'} (IČO: ${cb.ico || 'N/A'}) - Cashback</li>`;
      }
    });
    
    // Cashback strategické provize - zákazníci a produkty
    invoiceMonthDetail.cashbackStrategic.forEach(comm => {
      if (comm.commission !== 0) {
        const productType = comm.productType || comm.productName || 'strategický produkt';
        customerListItems += `<li style="margin-bottom: 3px;">${comm.companyName || 'N/A'} (IČO: ${comm.ico || 'N/A'}) - ${productType} (Cashback)</li>`;
      }
    });
    
    // HTML pro fakturu
    const duzpDate = new Date(Number(year), Number(month), 0).toLocaleDateString('cs-CZ');

    const invoiceHTML = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Faktura - Provize ${monthName}</title>
          <style>
            @page { size: A4; margin: 15mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; font-size: 10px; }
            .header { text-align: center; margin-bottom: 15px; }
            .header h1 { font-size: 20px; margin: 5px 0; }
            .header p { font-size: 9px; margin: 2px 0; }
            .company-info { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 9px; }
            .company-info div { width: 48%; }
            .company-info h3 { font-size: 11px; margin: 0 0 5px 0; }
            .company-info p { margin: 2px 0; line-height: 1.3; }
            .items { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 9px; }
            .items th { border: 1px solid #ddd; padding: 4px; background-color: #f2f2f2; text-align: left; font-size: 9px; }
            .items td { border: 1px solid #ddd; padding: 4px; }
            .total { text-align: right; font-weight: bold; font-size: 11px; margin: 10px 0; }
            .total-row { background-color: #f9f9f9; }
            .invoice-details { margin: 10px 0; font-size: 9px; }
            .qr-code { text-align: center; margin-top: 10px; }
            .qr-code p { font-size: 9px; margin: 5px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FAKTURA</h1>
            <p>Číslo faktury: ${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}</p>
            <p>Datum vystavení: ${new Date().toLocaleDateString('cs-CZ')}</p>
            <p>Datum splatnosti: ${(() => {
              const dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + 30);
              return dueDate.toLocaleDateString('cs-CZ');
            })()}</p>
            ${invoiceData.isVatPayer ? `<p>DUZP: ${duzpDate}</p>` : ''}
          </div>
          
          <div class="company-info">
            <div>
              <h3>Dodavatel:</h3>
              <p><strong>${invoiceData.supplierName}</strong></p>
              <p>${invoiceData.supplierAddress}</p>
              <p>Číslo účtu: ${invoiceData.accountNumber}</p>
              <p>IČO: ${invoiceData.supplierIco}</p>
              ${invoiceData.supplierDic ? `<p>DIČ: ${invoiceData.supplierDic}</p>` : ''}
              <p>${invoiceData.isVatPayer ? '<strong>Plátce DPH</strong>' : '<strong>Neplátce DPH</strong>'}</p>
            </div>
            <div>
              <h3>Odběratel:</h3>
              <p><strong>Vyjednáme, s.r.o.</strong></p>
              <p>Příčná 1892/4, Nové Město (Praha 1)</p>
              <p>110 00 Praha</p>
              <p>IČO: 21377596</p>
              <p>DIČ: CZ21377596</p>
            </div>
          </div>

          <table class="items">
            <thead>
              <tr>
                <th style="width: 80%;">Popis služby</th>
                <th style="width: 20%; text-align: right;">Částka</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="font-size: 10px; padding: 6px;">Zprostředkování obchodu a služeb - měsíc ${monthName}</td>
                <td style="font-size: 10px; padding: 6px; text-align: right;">${formatCurrency(baseAmount)} Kč</td>
              </tr>
              ${invoiceData.isVatPayer ? `
              <tr class="total-row">
                <td style="text-align: right; font-weight: bold; font-size: 10px; padding: 6px;">DPH 21%:</td>
                <td style="text-align: right; font-weight: bold; font-size: 10px; padding: 6px;">${formatCurrency(vatAmount)} Kč</td>
              </tr>
              ` : ''}
              <tr class="total-row">
                <td style="text-align: right; font-weight: bold; font-size: 11px; padding: 6px;">Celkem ${invoiceData.isVatPayer ? 's DPH' : ''}:</td>
                <td style="text-align: right; font-weight: bold; font-size: 11px; padding: 6px;">${formatCurrency(invoiceData.isVatPayer ? totalAmount : baseAmount)} Kč</td>
              </tr>
            </tbody>
          </table>

          ${customerListItems ? `
          <div style="margin: 15px 0; padding: 10px; background-color: #f9f9f9; border-left: 3px solid #2196F3;">
            <p style="font-size: 9px; margin: 0 0 8px 0;"><strong>Zprostředkování pro následující zákazníky a produkty:</strong></p>
            <ul style="font-size: 8px; margin: 0; padding-left: 20px; line-height: 1.5; color: #555;">
              ${customerListItems}
            </ul>
          </div>
          ` : ''}

          <div class="total">
            <p>Celková částka k úhradě: <strong>${formatCurrency(invoiceData.isVatPayer ? totalAmount : baseAmount)} Kč</strong></p>
          </div>

          <div class="invoice-details">
            <p><strong>Platební údaje:</strong></p>
            <p>Číslo účtu: ${invoiceData.accountNumber}</p>
            <p>Variabilní symbol: ${invoiceData.variableSymbol}</p>
          </div>

          <div class="qr-code">
            <p><strong>QR kód pro platbu:</strong></p>
            <img src="${generateQRCode()}" alt="QR kód pro platbu" />
          </div>
        </body>
      </html>
    `;

    // Otevři v novém okně pro tisk
    const printWindow = window.open('', '_blank');
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const saveInvoiceData = () => {
    // Uložit do localStorage pro příští použití
    localStorage.setItem('invoiceData', JSON.stringify(invoiceData));
    alert('Fakturační údaje uloženy!');
    setShowInvoiceGenerator(false);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Chyba při načítání uživatelů:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchSummaryData();
    
    // Načti uložená fakturační data
    const savedData = localStorage.getItem('invoiceData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setInvoiceData({
        ...parsed,
        isVatPayer: parsed.isVatPayer || false // Zajisti výchozí hodnotu
      });
    }
  }, [currentUserId, selectedUser]);

  useEffect(() => {
    if (invoiceMonth) {
      fetchInvoiceMonthData(invoiceMonth);
    }
  }, [invoiceMonth, currentUserId, selectedUser]);

  const maxAmount = Math.max(...monthlyData.map(d => d.amount), 1);

  return (
    <div>
      {/* Filtr uživatele - pouze pro admin */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          {/* Levá strana - Filtr uživatelů (pouze admin) */}
          {userRole === 'admin' && (
            <div className="flex items-center space-x-4">
              <label htmlFor="user-filter" className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Filtrovat podle uživatele:
              </label>
              <select
                id="user-filter"
                value={selectedUser || ''}
                onChange={(e) => setSelectedUser(e.target.value || null)}
                className={`mt-1 block w-48 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Všichni uživatelé</option>
                {allUsers.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Pravá strana - Generátor faktury a cashback tlačítko */}
          <div className="flex items-center gap-3 ml-auto">
            {/* Tlačítko pro zaplacení cashbacku ze zádržného (pouze admin) */}
            {userRole === 'admin' && lastMonthBreakdown.cashback < 0 && (
              <button
                onClick={payCashbackFromRetention}
                disabled={loading}
                className="px-4 py-2 rounded-lg font-medium transition-colors bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
              >
                💰 Zaplatit Cashback ze zádrženého
                <span className="ml-2 text-sm">({formatNumber(Math.abs(lastMonthBreakdown.cashback))} Kč)</span>
              </button>
            )}
            
            {/* Generátor faktury - pro všechny */}
            <div className="flex items-center gap-3">
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mr-2`}>Měsíc:</label>
                <input
                  type="month"
                  value={invoiceMonth}
                  onChange={(e) => setInvoiceMonth(e.target.value)}
                  className={`border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              <button
                onClick={() => setShowInvoiceGenerator(true)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 font-medium"
              >
                📄 Vystavit fakturu
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detailní rozklad provizí a přehled zádržného */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Minulý měsíc - rozklad */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>
              Rozklad za minulý měsíc ({new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })})
            </h3>
            <div className="space-y-3">
              {/* BMSL - podepsáno */}
              <div 
                className={`flex justify-between items-center p-3 rounded border-l-4 border-green-500 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-green-50 hover:bg-green-100'} cursor-pointer transition-colors`}
                onClick={() => {
                  if (lastMonthBmsl.companyCount > 0) {
                    setBmslModalData({
                      companies: lastMonthBmsl.companies,
                      month: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' }),
                      isLastMonth: true
                    });
                    setShowBmslModal(true);
                  }
                }}
              >
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Podepsáno BMSL
                  {lastMonthBmsl.companyCount > 0 && (
                    <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ({lastMonthBmsl.companyCount} {lastMonthBmsl.companyCount === 1 ? 'firma' : lastMonthBmsl.companyCount < 5 ? 'firmy' : 'firem'})
                    </span>
                  )}
                </span>
                <span className="font-bold text-green-600">{formatNumber(lastMonthBmsl.totalBmsl)} Kč</span>
              </div>
              {/* GP - aktivováno */}
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-orange-500 ${isDarkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Aktivováno (GP)</span>
                <span className="font-bold text-orange-600">{formatNumber(lastMonthBreakdown.gp)} Kč</span>
              </div>
              
              {/* Oddělovač mezi BMSL/GP a provizemi */}
              <div className={`border-t-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} my-2`}></div>
              
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-blue-500 ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Hlavní provize</span>
                <span className="font-bold text-blue-600">{formatNumber(lastMonthBreakdown.main)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-purple-500 ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Strategické provize</span>
                <span className="font-bold text-purple-600">{formatNumber(lastMonthBreakdown.strategic)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-red-500 ${isDarkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Cashback</span>
                <span className="font-bold text-red-600">{formatNumber(lastMonthBreakdown.cashback)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-orange-500 ${isDarkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Cashback - strategická provize</span>
                <span className="font-bold text-orange-600">{formatNumber(lastMonthBreakdown.cashbackStrategic)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-yellow-500 ${isDarkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Zádržné</span>
                <span className="font-bold text-yellow-600">-{formatNumber(lastMonthRetention)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-t-2 border-gray-300 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>K výplatě</span>
                <span className="font-bold text-green-600 text-lg">{formatNumber(lastMonthCommissions - lastMonthRetention)} Kč</span>
              </div>
            </div>
            
            {/* Moje koeficienty */}
            <div className={`mt-6 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                Moje koeficienty za minulý měsíc
              </h4>
              <div className="space-y-2">
                <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Hlavní provize:</span>
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {formatCoefficient(coefficients.mainCoefficient)}
                  </span>
                </div>
                <div className={`flex justify-between items-center p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Strategické provize:</span>
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    {formatCoefficient(coefficients.strategicCoefficient)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Aktuální měsíc - rozklad */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
            <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>
              Rozklad za aktuální měsíc ({new Date().toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })})
            </h3>
            <div className="space-y-3">
              {/* BMSL - podepsáno */}
              <div 
                className={`flex justify-between items-center p-3 rounded border-l-4 border-green-500 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-green-50 hover:bg-green-100'} cursor-pointer transition-colors`}
                onClick={() => {
                  if (currentMonthBmsl.companyCount > 0) {
                    setBmslModalData({
                      companies: currentMonthBmsl.companies,
                      month: new Date().toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' }),
                      isLastMonth: false
                    });
                    setShowBmslModal(true);
                  }
                }}
              >
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Podepsáno BMSL
                  {currentMonthBmsl.companyCount > 0 && (
                    <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ({currentMonthBmsl.companyCount} {currentMonthBmsl.companyCount === 1 ? 'firma' : currentMonthBmsl.companyCount < 5 ? 'firmy' : 'firem'})
                    </span>
                  )}
                </span>
                <span className="font-bold text-green-600">{formatNumber(currentMonthBmsl.totalBmsl)} Kč</span>
              </div>
              {/* GP - aktivováno */}
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-orange-500 ${isDarkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Aktivováno (GP)</span>
                <span className="font-bold text-orange-600">{formatNumber(currentMonthBreakdown.gp)} Kč</span>
              </div>
              
              {/* Oddělovač mezi BMSL/GP a provizemi */}
              <div className={`border-t-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} my-2`}></div>
              
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-blue-500 ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Hlavní provize</span>
                <span className="font-bold text-blue-600">{formatNumber(currentMonthBreakdown.main)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-purple-500 ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Strategické provize</span>
                <span className="font-bold text-purple-600">{formatNumber(currentMonthBreakdown.strategic)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-red-500 ${isDarkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Cashback</span>
                <span className="font-bold text-red-600">{formatNumber(currentMonthBreakdown.cashback)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-orange-500 ${isDarkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Cashback - strategická provize</span>
                <span className="font-bold text-orange-600">{formatNumber(currentMonthBreakdown.cashbackStrategic)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-l-4 border-yellow-500 ${isDarkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Zádržné (odhad)</span>
                <span className="font-bold text-yellow-600">-{formatNumber(currentMonthRetention)} Kč</span>
              </div>
              <div className={`flex justify-between items-center p-3 rounded border-t-2 border-gray-300 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>K výplatě (odhad)</span>
                <span className="font-bold text-orange-600 text-lg">{formatNumber(currentMonthCommissions - currentMonthRetention)} Kč</span>
              </div>
            </div>
          </div>

          {/* Přehled zádržného */}
          {monthlyRetentionData && monthlyRetentionData.length > 0 && (
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>
                Přehled zádržného
              </h3>
              
              {/* Aktuální hodnota zadržené provize */}
              <div className={`mb-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} border-l-4 border-blue-500`}>
                <div className="flex justify-between items-center">
                  <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Aktuální stav fondu:
                  </span>
                  <span className={`text-xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {(() => {
                      // Najdi nejnovější záznam s nenulovou hodnotou currentFundBalance
                      for (let i = monthlyRetentionData.length - 1; i >= 0; i--) {
                        if (monthlyRetentionData[i]?.currentFundBalance > 0) {
                          return formatNumber(monthlyRetentionData[i].currentFundBalance);
                        }
                      }
                      return '0';
                    })()} Kč
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className={`min-w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Měsíc
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Zadrženo
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Uvolněno
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                        Stav fondu
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                    {[...monthlyRetentionData]
                      .filter(data => (data.retentionAmount > 0 || data.releasedAmount > 0 || data.currentFundBalance > 0))
                      .reverse()
                      .slice(0, 12)
                      .map((data, index) => (
                      <tr key={index}>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                          {data.month}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                          -{formatNumber(data.retentionAmount || 0)} Kč
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                          {formatNumber(data.releasedAmount || 0)} Kč
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          {formatNumber(data.currentFundBalance || 0)} Kč
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Graf posledních 12 měsíců */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg shadow mb-8`}>
        <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Vývoj celkových provizí za posledních 12 měsíců</h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>Zahrnuje hlavní provize, strategické provize i cashback</p>
        {loading ? (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>Načítání grafu...</div>
        ) : (
          <div className="pb-8" style={{ minHeight: '300px' }}>
            <div className="grid grid-cols-6 lg:grid-cols-12 gap-2 items-end pt-8 pb-4" style={{ minHeight: '280px' }}>
              {monthlyData.map((data, index) => {
                // Rozděl "lis 2024" na měsíc a rok
                const [month, year] = data.month.split(' ');
                return (
                  <div key={index} className="flex flex-col justify-end items-center h-full relative">
                    <div
                      className="bg-blue-500 rounded-t w-full transition-all hover:bg-blue-600 cursor-pointer relative"
                      style={{ 
                        height: `${Math.max((data.amount / maxAmount) * 200, 5)}px`,
                        minHeight: '5px'
                      }}
                      title={`${month} ${year}: ${formatNumber(data.amount)} Kč`}
                    >
                      {/* Hodnota nad sloupcem */}
                      <div 
                        className={`text-[10px] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} font-semibold absolute whitespace-nowrap`}
                        style={{ 
                          top: '-16px',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }}
                      >
                        {formatNumber(data.amount)} Kč
                      </div>
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-2 text-center leading-tight w-full`}>
                      <div className="font-medium">{month}</div>
                      <div className="text-[10px]">{year}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal pro generátor faktury */}
      {showInvoiceGenerator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Generátor faktury</h2>
              <button
                onClick={() => setShowInvoiceGenerator(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Název dodavatele *
                  </label>
                  <input
                    type="text"
                    value={invoiceData.supplierName}
                    onChange={(e) => setInvoiceData({...invoiceData, supplierName: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Vaše jméno nebo název firmy"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Adresa dodavatele *
                  </label>
                  <input
                    type="text"
                    value={invoiceData.supplierAddress}
                    onChange={(e) => setInvoiceData({...invoiceData, supplierAddress: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Vaše adresa"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                      IČO *
                    </label>
                    <input
                      type="text"
                      value={invoiceData.supplierIco}
                      onChange={(e) => setInvoiceData({...invoiceData, supplierIco: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Vaše IČO"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                      DIČ
                    </label>
                    <input
                      type="text"
                      value={invoiceData.supplierDic}
                      onChange={(e) => setInvoiceData({...invoiceData, supplierDic: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Vaše DIČ (volitelné)"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                      Číslo účtu *
                    </label>
                    <input
                      type="text"
                      value={invoiceData.accountNumber}
                      onChange={(e) => setInvoiceData({...invoiceData, accountNumber: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Číslo vašeho účtu"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                      Variabilní symbol
                    </label>
                    <input
                      type="text"
                      value={invoiceData.variableSymbol}
                      onChange={(e) => setInvoiceData({...invoiceData, variableSymbol: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Variabilní symbol"
                    />
                  </div>
                </div>

                <div>
                  <label className={`flex items-center space-x-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <input
                      type="checkbox"
                      checked={invoiceData.isVatPayer}
                      onChange={(e) => setInvoiceData({...invoiceData, isVatPayer: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">Jsem plátce DPH</span>
                  </label>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Pokud jste plátce DPH, bude k faktuře přičteno 21% DPH
                  </p>
                </div>

                <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded`}>
                  <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Náhled faktury:</h4>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Měsíc:</strong> {(() => {
                    const [year, month] = invoiceMonth.split('-');
                    return new Date(year, month - 1).toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
                  })()}</p>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Provize celkem:</strong> {formatNumber(invoiceMonthCommissions)} Kč</p>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Zádržné:</strong> -{formatNumber(invoiceMonthRetention)} Kč</p>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Částka k výplatě (bez DPH):</strong> {formatNumber(invoiceMonthCommissions - invoiceMonthRetention)} Kč</p>
                  {invoiceData.isVatPayer && (
                    <>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>DPH 21%:</strong> {formatNumber((invoiceMonthCommissions - invoiceMonthRetention) * 0.21)} Kč</p>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Celkem s DPH:</strong> {formatNumber((invoiceMonthCommissions - invoiceMonthRetention) * 1.21)} Kč</p>
                    </>
                  )}
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}><strong>Služba:</strong> Zprostředkování obchodu a služeb</p>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={saveInvoiceData}
                    disabled={!invoiceData.supplierName || !invoiceData.supplierAddress || !invoiceData.supplierIco || !invoiceData.accountNumber}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    💾 Uložit údaje
                  </button>
                  <button
                    onClick={generateInvoicePDF}
                    disabled={!invoiceData.supplierName || !invoiceData.accountNumber}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    📄 Vygenerovat fakturu
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro seznam BMSL firem */}
      {showBmslModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Podepsané firmy BMSL - {bmslModalData.month}
              </h2>
              <button
                onClick={() => setShowBmslModal(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              {bmslModalData.companies.length === 0 ? (
                <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Žádné podepsané firmy v tomto měsíci
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                          Název firmy
                        </th>
                        <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                          Typ
                        </th>
                        <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                          IČO
                        </th>
                        <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                          Datum zobchodování
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                          BMSL
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                      {bmslModalData.companies.map((company, index) => (
                        <tr key={index} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {company.name}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              company.type === 'upsell' 
                                ? isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                                : isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {company.type === 'upsell' ? 'Upsell' : 'Hlavní'}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-sm text-center font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {company.ico || '-'}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {company.closedDate ? new Date(company.closedDate).toLocaleDateString('cs-CZ') : '-'}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            {formatNumber(company.bmsl)} Kč
                          </td>
                        </tr>
                      ))}
                      {/* Celkový řádek */}
                      <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} font-semibold`}>
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`} colSpan="4">
                          Celkem ({bmslModalData.companies.length} {bmslModalData.companies.length === 1 ? 'firma' : bmslModalData.companies.length < 5 ? 'firmy' : 'firem'})
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                          {formatNumber(bmslModalData.companies.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0))} Kč
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}